/**
 * Turning what an author typed into the two different URLs this feature needs.
 *
 * One field, two jobs, and they want opposite things:
 *
 *  - **matching**, in the visitor's browser, wants a *path*. The widget
 *    substring-matches `pages[].url` against the host document's location, so a
 *    path survives a domain change, a staging host, `www` vs no-`www` and a
 *    locale subdomain. A full address survives none of those.
 *  - **fetching**, for the preview and the section generator, wants an
 *    *absolute URL*. A path cannot be opened in a browser.
 *
 * So the author types `/pricing`, the teaser stores `/pricing`, and the site
 * lives once in `teaser.website_url`. Everything here is the arithmetic between
 * those.
 */

/** A hint is a path when it has no scheme and no host — the shape we want. */
export const isPathHint = (hint: string): boolean => {
  const trimmed = hint.trim();
  if (!trimmed) {
    return false;
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    return false;
  }
  // A dot before the first slash is a bare host (`moveo.ai/pricing`), which
  // matches in production but never inside the preview.
  const firstSegment = trimmed.replace(/^\/+/, '').split('/')[0];
  return !firstSegment.includes('.');
};

/** Normalises the site field: scheme required, no trailing slash, no path. */
export const normalizeWebsiteUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    // `origin` drops any path the author pasted, which is what we want: the
    // path belongs to the page row, and keeping both would double it up.
    return url.origin;
  } catch {
    return '';
  }
};

/** The path as it should be stored: leading slash, no origin. */
export const normalizePathHint = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    // An absolute URL is kept absolute — see `resolvePageUrl`. Authors paste
    // one when the page is on another domain, and rewriting it to a path would
    // silently point it at the wrong site.
    return trimmed;
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

/**
 * The absolute URL to render for a page row, or `null` when there isn't one.
 *
 * Returns `null` rather than a guess: a row with a path and no site configured
 * has nothing to fetch, and the caller shows that as "set your website first"
 * instead of sending a broken URL to the renderer.
 */
export const resolvePageUrl = (hint: string, websiteUrl: string): string | null => {
  const trimmed = hint.trim();
  if (!trimmed) {
    return null;
  }

  // An absolute hint is already the answer, and overrides the site — that is
  // the escape hatch for a page hosted somewhere else.
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    try {
      return new URL(trimmed).toString();
    } catch {
      return null;
    }
  }

  const base = normalizeWebsiteUrl(websiteUrl);
  if (!base) {
    return null;
  }

  try {
    return new URL(normalizePathHint(trimmed), `${base}/`).toString();
  } catch {
    return null;
  }
};

/**
 * Whether this row's section teasers can be observed in the preview.
 *
 * Only a path can: the preview document is served from tooling and its URL is
 * mirrored to end in the customer's path, so `/pricing` appears in it and
 * `https://moveo.ai/pricing` cannot. An absolute row still previews — the page
 * renders and the block teaser shows — but its page and section teasers will
 * not resolve, and the author needs telling that before they conclude the
 * feature is broken.
 */
export const matchesInPreview = (hint: string): boolean => isPathHint(hint);

/**
 * Splits what the author typed in the add-webpage field into the two halves the
 * teaser stores separately.
 *
 * The design gives a page one field holding a whole address — `moveo.ai/pricing`
 * after a fixed `https://` — but the stored shape is still an origin on the
 * block and a path on the row, for every reason in this file's header. So the
 * split happens here, once, at the moment the page is created, rather than
 * making the author understand it.
 *
 * Returns `null` when there is nothing usable to split, so the caller shows a
 * field error instead of storing half an address.
 */
export const splitPageUrl = (value: string): { origin: string; path: string } | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }
  // A bare host with no path is the site's home page, which is a page like any
  // other: `/` is a real hint and matches every URL on the site.
  const path = `${url.pathname || '/'}${url.search}${url.hash}`;
  return { origin: url.origin, path };
};

/**
 * A readable name for a page, from its path — `/debt-collection` becomes
 * "Debt collection". Empty for a path with no segments, i.e. the home page,
 * which has no name to derive; the caller falls back to the host there.
 *
 * The path is the whole source. Nothing reads the rendered document's own
 * `<title>`, and it does not need to: the author enters a full address, so the
 * last segment is the name they already have in mind for the page.
 */
export const titleFromPath = (path: string): string => {
  const last = path.split(/[?#]/)[0].split('/').filter(Boolean).pop();
  if (!last) {
    return '';
  }
  const words = decodeURIComponent(last)
    // Drop a file extension, so `/pricing.html` is not "Pricing html".
    .replace(/\.[a-z0-9]{1,5}$/i, '')
    .replace(/[-_+]+/g, ' ')
    .trim();
  if (!words) {
    return '';
  }
  return words.charAt(0).toUpperCase() + words.slice(1);
};
